import { Component, input, ViewChild } from '@angular/core';
import { environment } from '../../../environments/environment';
import { PoBreadcrumb, PoNotificationService, PoUploadComponent, PoUploadFile } from '@po-ui/ng-components';
import { forkJoin, of, tap } from 'rxjs';
import { HttpClient, HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-component-importacao',
  standalone: false,
  templateUrl: './importacao.component.html',
  styles: [`
    .upload-file-card {
      background: var(--color-neutral-light-00, #1e1e2e);
      border: 1px solid var(--color-neutral-light-10, #2a2a3e);
      border-radius: 8px;
      padding: 14px 16px 10px;
      margin-bottom: 8px;
      position: relative;
      overflow: hidden;
    }
    .upload-file-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }
    .upload-file-card-name {
      font-size: 13px;
      font-weight: 500;
      color: var(--color-neutral-dark-80, #ccc);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 75%;
    }
    .upload-file-card-status {
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
    }
    .status-uploading { color: var(--color-brand-01-base, #1ea7fd); }
    .status-processing { color: #ffab00; }
    .status-success { color: #00c853; }
    .status-error { color: #ff5252; }
    .upload-bar-track {
      width: 100%;
      height: 4px;
      background: var(--color-neutral-light-10, #333);
      border-radius: 2px;
      overflow: hidden;
    }
    .upload-bar-fill {
      height: 100%;
      border-radius: 2px;
      transition: width 0.3s ease;
      background: var(--color-brand-01-base, #1ea7fd);
    }
    .upload-bar-fill.bar-processing {
      background: #ffab00;
      animation: pulse-bar 1.2s ease-in-out infinite;
    }
    .upload-bar-fill.bar-success { background: #00c853; }
    .upload-bar-fill.bar-error { background: #ff5252; }
    @keyframes pulse-bar {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `]
})
export class ImportacaoComponent {
  @ViewChild('upload', { static: true }) upload!: PoUploadComponent;
  rotas = input<{ tabela: string, url: string }[]>();

  loading = false;
  restrictions = { allowedExtensions: ['.xlsx', '.csv'] };
  urlPlaceholder = 'https://po-ui.io'; // valor qualquer, obrigatório, mas não usado
  checkbox: boolean | null = false;
  changeDetectorRef: any;

  fileProgress: Map<string, number> = new Map();
  fileStatus: Map<string, 'uploading' | 'processing' | 'success' | 'error'> = new Map();
  fileList: { name: string; size: string }[] = [];

  constructor(
    private http: HttpClient,
    private notification: PoNotificationService
  ) { }

  sendArquivos() {
    const files = this.upload?.currentFiles;

    if (!files?.length) {
      this.notification.warning('Selecione pelo menos um arquivo.');
      return;
    }

    this.loading = true;
    this.fileProgress.clear();
    this.fileStatus.clear();
    this.fileList = files.map((f: any) => ({ name: f.name, size: this.formatSize(f.rawFile?.size || 0) }));

    const requests = files.map((file: any) => {
      const url = this.verificarTabelaDoArquivo(file);

      if (url === 'Desconhecido') {
        this.notification.error(`Arquivo ${file.name} não reconhecido.`);
        return of(null);
      }

      const formData = new FormData();
      formData.append('arquivo', file.rawFile!, file.name);
      formData.append('cleanDatabase', this.checkbox ? 'true' : 'false');

      this.fileProgress.set(file.name, 0);
      this.fileStatus.set(file.name, 'uploading');

      return this.http.post(url, formData, {
        reportProgress: true,
        observe: 'events'
      }).pipe(
        tap({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              const percent = event.total ? Math.round(100 * event.loaded / event.total) : 0;
              this.fileProgress.set(file.name, percent);
              if (percent >= 100) {
                this.fileStatus.set(file.name, 'processing');
              }
            } else if (event.type === HttpEventType.Response) {
              this.fileProgress.set(file.name, 100);
              this.fileStatus.set(file.name, 'success');
              file.status = 0;
              const response = event.body;
              this.notification.success(`Arquivo ${file.name} enviado com sucesso. [Registros importados: ${response?.processedRecords}]`);
            }
          },
          error: (err) => {
            this.fileProgress.set(file.name, 0);
            this.fileStatus.set(file.name, 'error');
            file.status = 2;
            this.notification.error(`Erro ao enviar ${file.name}: ${err.message}`);
            return of(null);
          }
        })
      );
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.finalizarEnvio();
      },
      error: () => {
        this.finalizarEnvio();
      },
      complete: () => {
        this.finalizarEnvio();
      }
    });
  }

  private finalizarEnvio() {
    this.loading = false;
    this.checkbox = false;
    this.upload.clear();
    setTimeout(() => {
      this.fileList = [];
      this.fileProgress.clear();
      this.fileStatus.clear();
    }, 5000);
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  verificarTabelaDoArquivo(file: PoUploadFile): string {
    const rotas = this.rotas?.();
    if (rotas) {
      for (const item of rotas) {
        const codigo = item.tabela.replace(/[\[\]]/g, '');
        if (this.nomeContemCodigo(file.name, codigo)) {
          return item.url;
        }
      }
    }
    return 'Desconhecido';
  }

  private nomeContemCodigo(fileName: string, codigo: string): boolean {
    const escaped = codigo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(?:^|[^A-Za-z0-9])\\[?${escaped}\\]?(?:[^A-Za-z0-9]|$)`, 'i');
    return pattern.test(fileName);
  }
}
